import type { HeaderStat } from '../types';

/**
 * True when a figure should produce no chip at all.
 *
 * Absent, empty — and **zero**. A column whose aggregate is zero has nothing to report, and a chip
 * reading `0` is noise the design does not want; the column shows a plain header instead.
 *
 * `parseFloat` rather than `Number` so a pre-formatted figure is read by its leading number: `'0'`,
 * `'0.00'` and `'0%'` are all zero and dropped, while `'47.11%'` keeps its chip. A value that parses
 * to `NaN` — a label like `'N/A'` — is deliberately kept, since the caller clearly meant to show it.
 */
export const isBlankStat = (value: HeaderStat['value']): boolean => {
  if (value === null || value === undefined || value === '') return true;
  return (typeof value === 'number' ? value : parseFloat(value)) === 0;
};

/**
 * The aggregate chip pinned above a column header (Figma "Costings to do" stat chips).
 *
 * Renders nothing for a nullish or empty value, so a column whose figure is unavailable shows a
 * plain header rather than an empty chip. It sits INSIDE the header cell and is lifted out of it by
 * `.cag-header-stat`'s absolute offset — that is what makes it survive column reordering,
 * horizontal scroll and column virtualisation without any offset bookkeeping, because AG Grid
 * creates, moves and destroys it along with the header cell it belongs to.
 *
 * The escape from the header cell depends on the `overflow: visible` overrides in
 * BombiixAGGrid.scss; AG Grid's own stylesheet clips header content at three nested levels.
 */
function HeaderStatChip({ value, tone = 'sum' }: HeaderStat) {
  if (isBlankStat(value)) return null;
  return <span className={`cag-header-stat cag-header-stat--${tone}`}>{value}</span>;
}

export default HeaderStatChip;
